import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import getValidationErrors from '../../../utils/getValidationErrors';

import {
  IconNotes,
  IconChevronRight,
  IconApps,
  IconTrash,
} from '@tabler/icons';

import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import api from '../../../services/api';

import BackHeader from '../../../components/BackHeader';
import Button from '../../../components/Button';
import Input from '../../../components/Form/Input';
import Modal from '../../../components/Modal';
import Offline from '../../../components/OfflineMessage';
import { H2, Skeleton, DeleteButton } from '../../../components/Generic';
import { Container, Content, ListItem } from './styles';

interface IBreed {
  nome: string;
}

const Breeds: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [breeds, setBreeds] = useState<any>([]);
  const [selectedBreed, setSelectedBreed] = useState<any>({});
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const newFormRef = useRef<FormHandles>(null);
  const editFormRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const isOnline = useOnlineStatus();
  const { id }: any = useParams();

  useEffect(() => {
    getBreeds();
  }, []);

  const getBreeds = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/racas/${id}`, {
        headers: {
          propriedade_id: id,
        },
      });

      setBreeds(response.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBreed = async (data: IBreed) => {
    try {
      setLoading(true);

      newFormRef.current?.setErrors({});

      const schema = Yup.object().shape({
        nome: Yup.string().required('Campo obrigatório.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await api.post(
        `/racas`,
        {
          nome: data.nome,
        },
        {
          headers: {
            propriedade_id: id,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Raça criada.',
      });

      getBreeds();
      setIsNewModalOpen(false);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        newFormRef.current?.setErrors(errors);
        return;
      }

      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditBreed = async (data: IBreed) => {
    try {
      setLoading(true);

      editFormRef.current?.setErrors({});

      const schema = Yup.object().shape({
        nome: Yup.string().required('Campo obrigatório.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await api.put(
        `/racas`,
        {
          nome: data.nome,
        },
        {
          headers: {
            propriedade_id: id,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Raça alterada.',
      });

      getBreeds();
      setIsEditModalOpen(false);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        editFormRef.current?.setErrors(errors);
        return;
      }

      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBreed = async () => {
    try {
      setLoading(true);

      await api.delete(`/racas/${selectedBreed.id}`, {
        headers: {
          propriedade_id: id,
        },
      });

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Raça excluída.',
      });

      getBreeds();
      setIsEditModalOpen(false);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        newFormRef.current?.setErrors(errors);
        return;
      }

      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <BackHeader title="Raças" />

      {!isOnline ? (
        <Offline />
      ) : loading ? (
        <Content>
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
        </Content>
      ) : (
        <Content>
          <Button
            onClick={() => {
              setIsNewModalOpen(true);
              newFormRef?.current?.reset();
            }}
            style={{ marginBottom: 40 }}
          >
            <IconApps size={30} color="#fff" style={{ marginRight: 20 }} />
            Adicionar raça
          </Button>

          {breeds?.map((breed: any) => (
            <ListItem
              key={breed.id}
              onClick={() => {
                setIsEditModalOpen(true);
                setSelectedBreed(breed);
              }}
            >
              <div>
                <IconNotes size={25} color="#333" style={{ marginRight: 20 }} />
                {breed.nome}
              </div>

              <IconChevronRight size={25} color="#333" />
            </ListItem>
          ))}
        </Content>
      )}

      <Modal isOpen={isNewModalOpen} onClose={() => setIsNewModalOpen(false)}>
        <H2>Nova raça</H2>

        <Form ref={newFormRef} onSubmit={handleCreateBreed}>
          <Input label="Nome" name="nome" type="text" required />

          <Button isLoading={loading} type="submit">
            Salvar
          </Button>
        </Form>
      </Modal>

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <H2>Editar raça</H2>

        <Form ref={editFormRef} onSubmit={handleEditBreed}>
          <Input
            label="Nome"
            name="nome"
            type="text"
            value={selectedBreed?.nome}
            onChange={e =>
              setSelectedBreed({ ...selectedBreed, nome: e.target.value })
            }
            required
          />

          <Button isLoading={loading} type="submit">
            Salvar
          </Button>
        </Form>

        <DeleteButton type="button" onClick={handleDeleteBreed}>
          <IconTrash size={25} color="#999" style={{ marginRight: 10 }} />
          Excluir
        </DeleteButton>
      </Modal>
    </Container>
  );
};

export default Breeds;
